// Permission mapping for menu items
export const menuItemPermissions = {
  'Dashboard': [], // Always visible
  'Users': ['view_users'],
  'Online Users': ['view_online_users'],
  'All Notifications': ['view_notifications'],
  'All Transactions': ['view_transactions'],
  'Balance': ['view_balance'],
  'All Chats': ['view_chats'],
  'Push Notifications': ['view_notifications'],
  'Smart Notifications': ['view_smart_notifications'],
  'Newsletter': ['view_notifications'],
  'Adverts': ['view_notifications'],
  'KYC Verification': ['view_kyc'],
  'Referral Management': ['view_referrals'],
  'Activities': ['view_activities'],
  'Settings': ['view_settings'],
  'All Admins': ['view_admins'],
  'Vendor Applications': ['view_vendors'],
  'Event Management': ['view_events'],
  'Event Design Templates': ['view_event_designs'],
  'Withdrawal Requests': ['view_withdrawals'],
  'ATM Card Management': ['view_atm_cards'],
  'Savings Management': ['view_savings'],
  'Spin & Win': ['view_spin'],
  'Beneficiary Groups': ['view_beneficiary_groups'],
  'Earnings': ['view_financial_data'],
  'Affiliate Management': ['view_affiliates'],
  'Media Management': ['view_media'],
  'Online Admins': ['view_online_admins'],
  'Funding Approvals': ['view_funding_approvals'],
  'System Logs': ['view_system_logs'],
  'Suspicious Balances': ['view_suspicious_balances'],
  'Deleted Transactions': ['view_deleted_transactions'],
};

/**
 * Check if user has a specific action permission
 * @param {string} permission - The permission to check (e.g., 'approve_withdrawals', 'delete_users')
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} userType - User type (superadmin, admin, agent)
 * @returns {boolean}
 */
export const hasActionPermission = (permission, userPermissions = [], userType = 'admin') => {
  // Superadmin always has all permissions
  if (userType === 'superadmin') {
    return true;
  }

  // Check if user has the specific permission
  return userPermissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} permissions - Array of permissions to check
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} userType - User type
 * @returns {boolean}
 */
export const hasAnyPermission = (permissions = [], userPermissions = [], userType = 'admin') => {
  // Superadmin always has all permissions
  if (userType === 'superadmin') {
    return true;
  }

  // Check if user has at least one of the required permissions
  return permissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} permissions - Array of permissions to check
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} userType - User type
 * @returns {boolean}
 */
export const hasAllPermissions = (permissions = [], userPermissions = [], userType = 'admin') => {
  // Superadmin always has all permissions
  if (userType === 'superadmin') {
    return true;
  }

  // Check if user has all required permissions
  return permissions.every(permission => userPermissions.includes(permission));
};

/**
 * Check if user has permission to view a menu item
 * @param {string} menuItem - The menu item name
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} userType - User type (superadmin, admin, agent)
 * @returns {boolean}
 */
export const hasPermission = (menuItem, userPermissions = [], userType = 'admin') => {
  // Superadmin always has access
  if (userType === 'superadmin') {
    return true;
  }

  // Get required permissions for this menu item
  const requiredPermissions = menuItemPermissions[menuItem] || [];

  // If no permissions required, allow access
  if (requiredPermissions.length === 0) {
    return true;
  }

  // Ensure userPermissions is an array
  const permissionsArray = Array.isArray(userPermissions) ? userPermissions : [];

  // Debug logging for Affiliate Management (remove in production)
  if (menuItem === 'Affiliate Management' && process.env.NODE_ENV === 'development') {
    console.log('Permission Check - Menu Item:', menuItem);
    console.log('Permission Check - Required Permissions:', requiredPermissions);
    console.log('Permission Check - User Permissions:', permissionsArray);
    console.log('Permission Check - Has Required Permission:', requiredPermissions.some(permission => permissionsArray.includes(permission)));
  }

  // Check if user has at least one of the required permissions
  return requiredPermissions.some(permission => permissionsArray.includes(permission));
};

/**
 * Filter menu items based on user permissions
 * @param {Array} menuItems - Array of menu items
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} userType - User type
 * @returns {Array} Filtered menu items
 */
export const filterMenuItems = (menuItems, userPermissions = [], userType = 'admin') => {
  const filtered = menuItems.filter(item => {
    const hasAccess = hasPermission(item.text, userPermissions, userType);

    // Debug logging for Affiliate Management
    if (item.text === 'Affiliate Management' && process.env.NODE_ENV === 'development') {
      console.log('filterMenuItems - Checking Affiliate Management');
      console.log('filterMenuItems - hasAccess:', hasAccess);
      console.log('filterMenuItems - userType:', userType);
      console.log('filterMenuItems - userPermissions:', userPermissions);
      console.log('filterMenuItems - requiredPermissions:', menuItemPermissions['Affiliate Management']);
    }

    return hasAccess;
  });

  return filtered;
};

